import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx-js-style';
import {
    X, Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, ArrowLeft, Download, XCircle,
} from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { MOTIVO_LABEL, STATUS_SOLICITACAO_LABEL, MotivoCancelamento } from '../../src/lib/cancelamentos';
import { parseAba, LinhaPlanilha } from '../../src/lib/cancelamentosImport';
import {
    montarPrevia, gravarLote, PreviaImportacao, LinhaResolvida, ResultadoGravacao,
} from '../../src/lib/cancelamentosImportacao';
import { exportarPendencias } from '../../src/lib/cancelamentosExport';
import ConfirmModal from '../../src/components/ConfirmModal';
import { propsFundoModal } from '../../src/lib/backdropModal';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onImported: () => void;
}

/**
 * Abas que não são lista de solicitação. "AJUSTE DE INJEÇÃO DE CREDITO" é
 * outro processo (não é cancelamento) e "INADIMPLENTE" é acompanhamento de
 * cobrança — entram como fonte de consulta, não como solicitação.
 */
const ABAS_IGNORADAS = /^(AJUSTE|INADIMPLENTE)/i;

const RESULTADO_META = {
    pronta: { label: 'Prontas', cls: 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50' },
    divergente: { label: 'Divergentes', cls: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50' },
    duplicada: { label: 'Já existentes', cls: 'text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700' },
    sem_uc: { label: 'Sem UC no cadastro', cls: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50' },
} as const;

const fmtData = (v: string | null) => {
    if (!v) return '—';
    const [a, m, d] = v.slice(0, 10).split('-');
    return d ? `${d}/${m}/${a}` : '—';
};

const ImportarCancelamentosModal: React.FC<Props> = ({ isOpen, onClose, onImported }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);

    const [etapa, setEtapa] = useState<1 | 2 | 3>(1);
    const [processando, setProcessando] = useState(false);
    const [nomeArquivo, setNomeArquivo] = useState('');
    const [previa, setPrevia] = useState<PreviaImportacao | null>(null);
    const [resultado, setResultado] = useState<ResultadoGravacao | null>(null);
    const [incluirDivergentes, setIncluirDivergentes] = useState(false);
    // Correções de categoria feitas pelo usuário, por chave de linha.
    const [motivoAjustado, setMotivoAjustado] = useState<Record<string, MotivoCancelamento>>({});
    // Categoria escolhida para aplicar em massa (etapa 2) — evita ter que
    // corrigir uma por uma quando a planilha tem ~200 linhas.
    const [motivoEmMassa, setMotivoEmMassa] = useState<MotivoCancelamento | ''>('');
    // Guarda a ação (fechar/trocar arquivo) pendente de confirmação quando há
    // correções de categoria feitas à mão que seriam perdidas.
    const [confirmarDescarte, setConfirmarDescarte] = useState<null | 'fechar' | 'trocar'>(null);

    const chaveLinha = (l: LinhaResolvida) => `${l.origem.aba}#${l.origem.linha}`;

    const reiniciar = useCallback(() => {
        setEtapa(1); setPrevia(null); setResultado(null); setNomeArquivo('');
        setIncluirDivergentes(false); setMotivoAjustado({}); setMotivoEmMassa('');
    }, []);

    const temEdicaoPendente = etapa === 2 && Object.keys(motivoAjustado).length > 0;

    const fechar = () => {
        if (processando) return;
        if (temEdicaoPendente) { setConfirmarDescarte('fechar'); return; }
        reiniciar(); onClose();
    };

    const pedirTrocarArquivo = () => {
        if (temEdicaoPendente) { setConfirmarDescarte('trocar'); return; }
        reiniciar();
    };

    const confirmarDescarteAcao = () => {
        const acao = confirmarDescarte;
        setConfirmarDescarte(null);
        reiniciar();
        if (acao === 'fechar') onClose();
    };

    // Aplica a categoria escolhida a todas as linhas que hoje entrariam na
    // importação (prontas, e divergentes se a opção estiver marcada). Não
    // sobrescreve o que o usuário já revisou individualmente antes de clicar.
    const aplicarMotivoEmMassa = () => {
        if (!previa || !motivoEmMassa) return;
        setMotivoAjustado(m => {
            const novo = { ...m };
            previa.linhas.forEach(l => {
                const entra = l.resultado === 'pronta' || (incluirDivergentes && l.resultado === 'divergente');
                if (entra) novo[chaveLinha(l)] = motivoEmMassa;
            });
            return novo;
        });
    };

    const processarArquivo = async (file: File) => {
        setProcessando(true);
        setNomeArquivo(file.name);
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: 'array', cellDates: false, raw: false });

            const linhas: LinhaPlanilha[] = [];
            wb.SheetNames.forEach(nome => {
                if (ABAS_IGNORADAS.test(nome.trim())) return;
                const matriz = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[nome], {
                    header: 1, raw: false, defval: '',
                });
                linhas.push(...parseAba(nome, matriz));
            });

            if (linhas.length === 0) {
                addToast('Nenhuma linha de cancelamento encontrada — confira se o arquivo tem a coluna "Unidade Consumidora".', 'warning');
                return;
            }
            setPrevia(await montarPrevia(linhas));
            setEtapa(2);
        } catch (error: any) {
            console.error('Erro ao ler planilha de cancelamentos', error);
            addToast('Erro ao ler a planilha: ' + (error.message || 'arquivo inválido'), 'error');
        } finally {
            setProcessando(false);
        }
    };

    const selecionadas = useMemo(() => {
        if (!previa) return [];
        return previa.linhas
            .filter(l => l.resultado === 'pronta' || (incluirDivergentes && l.resultado === 'divergente'))
            .map(l => ({ ...l, motivoCategoria: motivoAjustado[chaveLinha(l)] ?? l.motivoCategoria }));
    }, [previa, incluirDivergentes, motivoAjustado]);

    const confirmar = async () => {
        if (selecionadas.length === 0) {
            addToast('Nenhuma linha selecionada para importar.', 'warning');
            return;
        }
        setProcessando(true);
        try {
            const r = await gravarLote(selecionadas, nomeArquivo, user?.id);
            setResultado(r);
            setEtapa(3);
            onImported();
        } catch (error: any) {
            console.error('Erro ao gravar lote de cancelamentos', error);
            addToast('Erro ao importar: ' + (error.message || ''), 'error');
        } finally {
            setProcessando(false);
        }
    };

    const baixarPendencias = () => {
        if (!previa) return;
        exportarPendencias(
            previa.linhas
                .filter(l => l.resultado === 'sem_uc' || l.resultado === 'duplicada')
                .map(l => ({
                    aba: l.origem.aba, linha: l.origem.linha, ucBruta: l.origem.ucBruta,
                    cliente: l.origem.cliente, motivo: l.impedimento || '',
                })),
        );
    };

    // Esc fecha o modal (respeitando a confirmação de descarte quando há
    // edição pendente) — sem isso o teclado fica preso dentro do fluxo.
    React.useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape' && !confirmarDescarte) fechar(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, processando, temEdicaoPendente, confirmarDescarte]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
            {...propsFundoModal(() => fechar())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden flex flex-col max-h-[92vh]"
                role="dialog" aria-modal="true" aria-labelledby="importar-cancelamentos-titulo">
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 text-primary rounded-lg border border-primary/20">
                            <FileSpreadsheet size={20} />
                        </div>
                        <div>
                            <h2 id="importar-cancelamentos-titulo" className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">
                                Importar Cancelamentos
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">
                                {etapa === 1 && 'Etapa 1 de 3 — envie a planilha'}
                                {etapa === 2 && 'Etapa 2 de 3 — confira antes de gravar'}
                                {etapa === 3 && 'Etapa 3 de 3 — resultado'}
                            </p>
                        </div>
                    </div>
                    <button onClick={fechar} aria-label="Fechar" disabled={processando}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-40">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {/* ---------------------------------------------------------- Etapa 1 */}
                    {etapa === 1 && (
                        <div className="space-y-4">
                            <div
                                onClick={() => !processando && inputRef.current?.click()}
                                onDragOver={e => e.preventDefault()}
                                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) processarArquivo(f); }}
                                className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-2xl p-10 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                                {processando ? (
                                    <Loader2 size={32} className="mx-auto text-primary animate-spin" />
                                ) : (
                                    <Upload size={32} className="mx-auto text-gray-400 dark:text-slate-500" />
                                )}
                                <p className="mt-3 text-sm font-semibold text-gray-700 dark:text-slate-200">
                                    {processando ? 'Lendo a planilha e conferindo no cadastro...' : 'Arraste a planilha ou clique para escolher'}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">XLSX ou CSV</p>
                                <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                                    onChange={e => { const f = e.target.files?.[0]; if (f) processarArquivo(f); e.target.value = ''; }} />
                            </div>
                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 p-4 text-xs text-gray-600 dark:text-slate-300 space-y-1.5">
                                <p className="font-bold text-gray-700 dark:text-slate-200">Como a leitura funciona</p>
                                <p>• Todas as abas do arquivo são lidas; as colunas são reconhecidas pelo nome, então layouts diferentes entre meses funcionam.</p>
                                <p>• Abas de <strong>ajuste de crédito</strong> e de <strong>inadimplência</strong> são ignoradas — não são solicitações de cancelamento.</p>
                                <p>• Nada é gravado agora: na próxima etapa você confere o que casou com o cadastro e o que não casou.</p>
                                <p>• Se preferir partir do formato certo, use o botão <strong>Exportar</strong> da tela — o arquivo gerado é exatamente o modelo aceito aqui.</p>
                            </div>
                        </div>
                    )}

                    {/* ---------------------------------------------------------- Etapa 2 */}
                    {etapa === 2 && previa && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {(Object.keys(RESULTADO_META) as Array<keyof typeof RESULTADO_META>).map(k => (
                                    <div key={k} className={`rounded-xl border px-4 py-3 ${RESULTADO_META[k].cls}`}>
                                        <p className="text-2xl font-bold">{previa.totais[k]}</p>
                                        <p className="text-xs font-semibold">{RESULTADO_META[k].label}</p>
                                    </div>
                                ))}
                            </div>

                            {previa.duplicadasNaPlanilha > 0 && (
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    {previa.duplicadasNaPlanilha} linha(s) repetida(s) dentro da própria planilha foram unificadas — ficou a mais completa.
                                </p>
                            )}

                            {previa.totais.divergente > 0 && (
                                <label className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-4 cursor-pointer">
                                    <input type="checkbox" checked={incluirDivergentes}
                                        onChange={e => setIncluirDivergentes(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-amber-600" />
                                    <span className="text-xs text-amber-800 dark:text-amber-300">
                                        <strong className="block text-sm mb-0.5">Importar também as {previa.totais.divergente} linhas divergentes</strong>
                                        A planilha trata essas UCs como canceladas, mas elas continuam ativas no cadastro — ou seja, seguem no rateio.
                                        Elas entram marcadas como divergentes na tela, para a equipe conferir a situação real. Fora isso, ficam de fora da importação.
                                    </span>
                                </label>
                            )}

                            {(previa.totais.sem_uc > 0 || previa.totais.duplicada > 0) && (
                                <button onClick={baixarPendencias}
                                    className="inline-flex items-center gap-2 text-xs font-bold text-primary hover:opacity-80">
                                    <Download size={14} /> Baixar planilha das {previa.totais.sem_uc + previa.totais.duplicada} linhas que não entram
                                </button>
                            )}

                            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 p-3">
                                <span className="text-xs font-semibold text-gray-600 dark:text-slate-300">
                                    Motivo em massa — aplica a todas as linhas que vão entrar (revise as exceções depois):
                                </span>
                                <select
                                    aria-label="Categoria para aplicar em massa"
                                    value={motivoEmMassa}
                                    onChange={e => setMotivoEmMassa(e.target.value as MotivoCancelamento | '')}
                                    className="border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-primary">
                                    <option value="">Selecione...</option>
                                    {Object.entries(MOTIVO_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                                </select>
                                <button type="button" onClick={aplicarMotivoEmMassa} disabled={!motivoEmMassa}
                                    className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 text-xs font-bold hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors">
                                    Aplicar a todas
                                </button>
                                {Object.keys(motivoAjustado).length > 0 && (
                                    <span className="text-[11px] text-gray-400 dark:text-slate-500">
                                        {Object.keys(motivoAjustado).length} linha(s) com categoria corrigida manualmente.
                                    </span>
                                )}
                            </div>

                            <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                <div className="overflow-auto max-h-[46vh]">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700 text-xs">
                                        <thead className="bg-gray-50 dark:bg-slate-800/60 sticky top-0 z-10">
                                            <tr>
                                                {['Situação', 'UC / Cliente', 'Data', 'Motivo (confirme)', 'Vendedor', 'Distrato', 'Saldo', 'Status', 'Observações da leitura'].map(h => (
                                                    <th key={h} className="px-3 py-2.5 text-left font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
                                            {previa.linhas.map(l => {
                                                const k = chaveLinha(l);
                                                const entra = l.resultado === 'pronta' || (incluirDivergentes && l.resultado === 'divergente');
                                                return (
                                                    <tr key={k} className={entra ? '' : 'opacity-50 bg-gray-50/60 dark:bg-slate-800/30'}>
                                                        <td className="px-3 py-2 whitespace-nowrap">
                                                            <span className={`inline-block px-2 py-0.5 rounded-md border font-bold ${RESULTADO_META[l.resultado].cls}`}>
                                                                {RESULTADO_META[l.resultado].label}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <p className="font-semibold text-gray-800 dark:text-slate-100">{l.origem.cliente || '—'}</p>
                                                            <p className="text-gray-400 dark:text-slate-500">UC {l.origem.ucBruta || '—'} · {l.origem.aba}, linha {l.origem.linha}</p>
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-slate-300">{fmtData(l.origem.dataSolicitacao)}</td>
                                                        <td className="px-3 py-2">
                                                            <select
                                                                aria-label={`Categoria do motivo — UC ${l.origem.ucBruta}`}
                                                                value={motivoAjustado[k] ?? l.motivoCategoria}
                                                                disabled={!entra}
                                                                onChange={e => setMotivoAjustado(m => ({ ...m, [k]: e.target.value as MotivoCancelamento }))}
                                                                className={`w-full border rounded-lg px-2 py-1 bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:border-primary ${l.origem.motivoConfianca === 'baixa' && !motivoAjustado[k]
                                                                    ? 'border-amber-400 dark:border-amber-600'
                                                                    : 'border-gray-200 dark:border-slate-700'}`}>
                                                                {Object.entries(MOTIVO_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                                                            </select>
                                                            {l.origem.motivoTexto && (
                                                                <p className="text-gray-400 dark:text-slate-500 mt-1 max-w-[260px] truncate" title={l.origem.motivoTexto}>{l.origem.motivoTexto}</p>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap">
                                                            {l.origem.vendedorNome
                                                                ? <span className={l.vendedorNaoEncontrado ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-slate-300'}>
                                                                    {l.origem.vendedorNome}{l.vendedorNaoEncontrado ? ' (não encontrado)' : ''}
                                                                </span>
                                                                : <span className="text-gray-400 dark:text-slate-500">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-slate-300">
                                                            {l.termoLogId ? 'Termo no sistema'
                                                                : l.origem.distrato === 'sim' ? 'Sim (externo)'
                                                                    : l.origem.distrato === 'aguardando' ? 'Aguardando'
                                                                        : l.origem.distrato === 'nao' ? 'Não' : '—'}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-slate-300">
                                                            {l.origem.saldoKwh === null ? '—' : `${l.origem.saldoKwh.toLocaleString('pt-BR')} kWh`}
                                                        </td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 dark:text-slate-300">{STATUS_SOLICITACAO_LABEL[l.status]}</td>
                                                        <td className="px-3 py-2 text-gray-500 dark:text-slate-400 max-w-[300px]">
                                                            {[l.impedimento, ...l.origem.avisos].filter(Boolean).map((a, i) => <p key={i}>• {a}</p>)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ---------------------------------------------------------- Etapa 3 */}
                    {etapa === 3 && resultado && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20 p-5">
                                <CheckCircle2 size={28} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                                <div>
                                    <p className="text-lg font-bold text-emerald-800 dark:text-emerald-300">{resultado.gravadas} solicitação(ões) importada(s)</p>
                                    <p className="text-xs text-emerald-700 dark:text-emerald-400">
                                        O lote ficou identificado na tela e pode ser desfeito inteiro, se necessário.
                                    </p>
                                </div>
                            </div>
                            {resultado.falhas.length > 0 && (
                                <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-4">
                                    <p className="text-sm font-bold text-red-800 dark:text-red-300 flex items-center gap-2 mb-2">
                                        <XCircle size={16} /> {resultado.falhas.length} linha(s) recusada(s) pelo banco
                                    </p>
                                    <ul className="text-xs text-red-700 dark:text-red-400 space-y-1 max-h-48 overflow-y-auto">
                                        {resultado.falhas.map((f, i) => <li key={i}>UC {f.uc}: {f.erro}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                        {etapa === 2 && (
                            <span className="flex items-center gap-1.5">
                                {selecionadas.length === 0 && <AlertTriangle size={13} className="text-amber-500" />}
                                <strong className="text-gray-700 dark:text-slate-200">{selecionadas.length}</strong> linha(s) serão gravadas
                            </span>
                        )}
                        {nomeArquivo && etapa !== 2 && <span className="truncate">{nomeArquivo}</span>}
                    </div>
                    <div className="flex gap-3">
                        {etapa === 2 && (
                            <button onClick={pedirTrocarArquivo} disabled={processando}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50">
                                <ArrowLeft size={14} /> Trocar arquivo
                            </button>
                        )}
                        <button onClick={fechar} disabled={processando}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50">
                            {etapa === 3 ? 'Fechar' : 'Cancelar'}
                        </button>
                        {etapa === 2 && (
                            <button onClick={confirmar} disabled={processando || selecionadas.length === 0}
                                className="px-6 py-2 bg-primary hover:opacity-90 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 min-w-[180px]">
                                {processando ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                                {processando ? 'Importando...' : `Importar ${selecionadas.length}`}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={!!confirmarDescarte}
                onClose={() => setConfirmarDescarte(null)}
                onConfirm={confirmarDescarteAcao}
                variant="warning"
                title="Descartar correções feitas?"
                message={`Você corrigiu a categoria de ${Object.keys(motivoAjustado).length} linha(s) nesta prévia. ${confirmarDescarte === 'trocar' ? 'Trocar de arquivo' : 'Fechar agora'} descarta essas correções — elas não são salvas até você clicar em "Importar".`}
                confirmLabel={confirmarDescarte === 'trocar' ? 'Trocar arquivo mesmo assim' : 'Fechar mesmo assim'}
                cancelLabel="Continuar revisando"
            />
        </div>,
        document.body,
    );
};

export default ImportarCancelamentosModal;
